import { useEffect, useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation, useNavigationType, useNavigate } from 'react-router-dom';
import { getCategoryPageData } from '../api/cmsApi';

import CMSConsumerCarouselBanner from '../components/banner/CMSConsumerCarouselBanner';
import CMSConsumerFeaturedGallery from '../components/gallery/CMSConsumerFeaturedGallery';
import CMSConsumerNewArrivalsGrid from '../components/grid/CMSConsumerNewArrivalsGrid';
import CMSConsumerApplicationsGrid from '../components/grid/CMSConsumerApplicationsGrid';
import CMSConsumerCollectionsGrid from '../components/grid/CMSConsumerCollectionsGrid';
import TextHeader from '../../../components/textHeader/TextHeader';
import LoadingScreen from '../../../components/shared/loading/LoadingScreen';
import MissingPage from '../../../components/shared/errorMessages/MissingPage';
import { captureAnalytics } from '../../../utilities/Analytics';
import { Box, Fab, Typography } from "@mui/material";
import { KeyboardArrowUp } from '@mui/icons-material';
import { CMSPageData, ChildTileData } from '../types/CMSTypes';

function ApplicationTypeTemplate() {
  const { item_group, application_type } = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  // Back to top button state
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Create refs for each collection section
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // CMS Data Query - Uses application_type name as category name, always live for consumer
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-application-type-${application_type}`],
    () => getCategoryPageData(application_type || '', 'live'),
    {
      staleTime: 600000,
      enabled: !!application_type,
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Get CMS category (check both main_cms_category for overrides and cms_category for standard)
  const cmsCategory = cmsResponse?.main_cms_category || cmsResponse?.cms_category;

  // Get the children tile data from CMS
  const childrenFromCMS: ChildTileData[] = cmsData?.children_tile_data?.active || [];

  // Get nav_add_ons from cms_category (exists in both override and standard responses)
  const navAddOnsFromCategory: ChildTileData[] = cmsCategory?.navigation_add_ons || [];

  // Merge both sources - nav_add_ons won't duplicate if already in children_tile_data
  const childrenTileData: ChildTileData[] = [
    ...childrenFromCMS,
    ...navAddOnsFromCategory.filter(navAddOn =>
      // Only add if not already in children_tile_data
      !childrenFromCMS.some(child =>
        child.category_type === 'nav_add_on' &&
        child.category_name === navAddOn.category_name
      )
    )
  ];

  // Get the new arrivals data and filter published items only
  const rawNewArrivalsData = cmsData?.new_arrivals?.active || [];
  const newArrivalsData = rawNewArrivalsData
    .filter(item => item.is_published)
    .sort((a, b) => {
      const orderA = a.order_position || 999;
      const orderB = b.order_position || 999;
      return orderA - orderB;
    });

  // Get grouped collections data from CMS response
  const groupedCollections = cmsData?.grouped_collections || [];

  // Define interface for grouped collections
  interface GroupedCollection {
    item_group: string;
    item_group_name: string;
    tile_data: {
      active: ChildTileData[];
      shadow: ChildTileData[];
    };
  }

  // Scroll management
  useEffect(() => {
    const multifilterActive = sessionStorage.getItem('multifilter_active') === 'true';
    const multifilterPosition = multifilterActive ? sessionStorage.getItem('multifilter_scroll_position') : null;
    const changingFromCategory = sessionStorage.getItem('changing_from_category_to_application') === 'true';
    const fromFilterProduct = sessionStorage.getItem('fromFilterComponent') === 'true';

    // Returning from a PDP: FilterComponent restores the exact scroll position
    // (and re-loads enough items in "Show All"), so don't interfere here.
    if (navigationType === 'POP' && sessionStorage.getItem('multifilter_restore')) {
      return;
    }

    if (navigationType === 'POP') {
      if (fromFilterProduct &&
        (location.pathname.includes('/application_type/') || location.pathname.includes('/collection/'))) {
        requestAnimationFrame(() => {
          if (multifilterActive && multifilterPosition) {
            window.scrollTo({
              top: parseInt(multifilterPosition, 10),
              behavior: 'auto'
            });
          }
          sessionStorage.removeItem('fromFilterComponent');
        });
      }
      else if (multifilterActive && multifilterPosition) {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: parseInt(multifilterPosition, 10),
            behavior: 'auto'
          });
        });
      }
    } else {
      if (changingFromCategory) {
        sessionStorage.removeItem('changing_from_category_to_application');
        return;
      }

      if (location.search.includes('page=') ||
        location.search.includes('colours=') ||
        location.search.includes('types=') ||
        location.search.includes('design_style=') ||
        location.search.includes('brand=')) {
        return;
      }

      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.search, navigationType]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (!location.pathname.includes('/design/')) {
        sessionStorage.removeItem('multifilter_active');
        sessionStorage.removeItem('multifilter_scroll_position');
        sessionStorage.removeItem('multifilter_item_group');
      }
    };
  }, [location.pathname]);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData && application_type) {
      const title = cmsData.page_title || `application-type-${application_type}`;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, application_type, cmsSuccess, cmsData]);

  // Back to top button scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.pageYOffset > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredTilesData = childrenTileData.filter(tile => {
    // For nav_add_ons, check consumer conditions (ignore consumer_use_switch for filtering)
    if (tile.category_type === 'nav_add_on') {
      return tile.is_active === true &&
        tile.consumer_tile_display_switch === true &&
        tile.consumer_tile_media_single_url;
    }

    // For regular CMS tiles, filter out fabric tiles (item_group 005)
    const itemGroupCode = tile.item_group;

    // Skip fabric tiles entirely - we only want nav_add_ons for fabric on application_type pages
    if (itemGroupCode === '005' || tile.category_name?.toLowerCase() === 'fabric') {
      return false;
    }

    if (!itemGroupCode) return false;

    // Check if this item group has collections
    const collections = groupedCollections as GroupedCollection[];
    return collections.some(group => group.item_group === itemGroupCode);
  });

  // Sort by position_order
  const tilesData = [...filteredTilesData].sort((a, b) => {
    const orderA = a.position_order ?? 999;
    const orderB = b.position_order ?? 999;
    return orderA - orderB;
  });

  // Scroll to section on page load if section query parameter exists
  useEffect(() => {
    if (cmsSuccess && tilesData.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const sectionParam = searchParams.get('section');

      if (sectionParam) {
        setTimeout(() => {
          scrollToSection(sectionParam);
        }, 300);
      }
    }
  }, [cmsSuccess, location.search, tilesData.length]);

  const capitalizeItemGroup = (itemGroup: string): string => {
    return itemGroup.charAt(0).toUpperCase() + itemGroup.slice(1);
  };

  const scrollToSection = (itemGroupName: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('section', itemGroupName.toLowerCase());
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });

    const targetSection = sectionRefs.current[itemGroupName.toLowerCase()];
    if (targetSection) {
      const targetPosition = targetSection.offsetTop - 100;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const duration = 600;
      let start: number | null = null;

      const smoothScroll = (timestamp: number) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const percentage = Math.min(progress / duration, 1);

        const easeInOut = (t: number) =>
          t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        window.scrollTo(0, startPosition + distance * easeInOut(percentage));

        if (progress < duration) {
          requestAnimationFrame(smoothScroll);
        }
      };

      requestAnimationFrame(smoothScroll);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Helper function to get collections for a specific item group
  const getCollectionsForItemGroup = (itemGroupCode: string): ChildTileData[] => {
    const collections = groupedCollections as GroupedCollection[];

    const groupedCollection = collections.find(
      (group: GroupedCollection) => group.item_group === itemGroupCode
    );

    if (!groupedCollection) return [];

    const collectionsData = groupedCollection.tile_data?.active || [];
    const publishedCollections = collectionsData.filter(item => item.is_published);

    return [...publishedCollections].sort((a, b) => {
      const nameA = (a.collection || a.category_name || '').toLowerCase();
      const nameB = (b.collection || b.category_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  if (!application_type) {
    return <MissingPage />;
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option || 'carousel'}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video || null}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link || ''}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id || null}
              bannerWidth={cmsData.consumer_banner_width || 2880}
              bannerHeight={cmsData.consumer_banner_height || 1050}
              viewMode="live"
            />
          )}

          {/* Text Header */}
          <TextHeader
            heading={cmsData?.page_title || application_type}
            content={cmsData?.description || ''}
            isCentered={false}
            width="70%"
            marginTop={10}
            marginBottom={5}
          />

          {/* CMS Item Group Tiles - Includes nav_add_ons from CMS */}
          {tilesData.length > 0 && (
            <CMSConsumerApplicationsGrid
              childrenData={tilesData}
              spacingValue={4}
              marginTop={5}
              marginBottom={5}
              isHomeware={false}
              onItemClick={scrollToSection}
              enableScrollMode={true}
            />
          )}

          {/* CMS Consumer Featured Gallery */}
          {cmsData?.featured_collection_display_switch && cmsData?.featured_collection && (
            <CMSConsumerFeaturedGallery
              featuredCollection={cmsData.featured_collection}
              hasVideo={false}
              invert={false}
              marginTop={5}
              marginBottom={5}
              viewMode="live"
            />
          )}

          {/* CMS Item Group Collection Sections */}
          {(groupedCollections as GroupedCollection[]).map((groupedCollection) => {
            const itemGroupCode = groupedCollection.item_group;
            const itemGroupName = groupedCollection.item_group_name;

            if (!itemGroupCode || !itemGroupName) return null;

            const collectionsForGroup = getCollectionsForItemGroup(itemGroupCode);

            if (collectionsForGroup.length === 0) return null;

            const isFabric = itemGroupCode === '005' || itemGroupName.toLowerCase() === 'fabric';

            if (isFabric) {
              const applicationMap = new Map<string, ChildTileData[]>();

              collectionsForGroup.forEach(collection => {
                const application = collection.application || 'Other';
                if (!applicationMap.has(application)) {
                  applicationMap.set(application, []);
                }
                applicationMap.get(application)!.push(collection);
              });

              const sortedApplications = Array.from(applicationMap.entries()).sort((a, b) => {
                const [appA] = a;
                const [appB] = b;

                if (appA === 'Curtaining') return -1;
                if (appB === 'Curtaining') return 1;
                if (appA === 'Upholstery') return -1;
                if (appB === 'Upholstery') return 1;
                return appA.localeCompare(appB);
              });

              return (
                <div key={itemGroupCode}>
                  <div
                    ref={(el) => sectionRefs.current[itemGroupName.toLowerCase()] = el}
                    id={`section-${itemGroupName.toLowerCase()}`}
                  >
                    <CMSConsumerCollectionsGrid
                      collectionsData={[]}
                      applicationName={`${application_type} ${capitalizeItemGroup(itemGroupName)}`}
                      marginTop={8}
                      marginBottom={2}
                      viewMode="live"
                      showHeader={true}
                    />
                  </div>

                  {sortedApplications.map(([application, collections]) => (
                    <div
                      key={`${itemGroupCode}-${application}`}
                      ref={(el) => sectionRefs.current[application.toLowerCase()] = el}
                      id={`section-${application.toLowerCase()}`}
                    >
                      <CMSConsumerCollectionsGrid
                        collectionsData={collections}
                        applicationName={application}
                        marginTop={6}
                        marginBottom={8}
                        viewMode="live"
                        showHeader={true}
                        headerType="application"
                      />
                    </div>
                  ))}
                </div>
              );
            }

            return (
              <div
                key={itemGroupCode}
                ref={(el) => sectionRefs.current[itemGroupName.toLowerCase()] = el}
                id={`section-${itemGroupName.toLowerCase()}`}
              >
                <CMSConsumerCollectionsGrid
                  collectionsData={collectionsForGroup}
                  applicationName={`${application_type} ${capitalizeItemGroup(itemGroupName)}`}
                  marginTop={8}
                  marginBottom={8}
                  viewMode="live"
                  showHeader={true}
                />
              </div>
            );
          })}

          {/* CMS Consumer New Arrivals */}
          {newArrivalsData.length > 0 && (
            <CMSConsumerNewArrivalsGrid
              newArrivalsData={newArrivalsData}
              header="New arrivals"
              hasTitles={true}
              hasButtons={true}
              hasBackgroundText={false}
              spacingValue={4}
              marginTop={'5rem'}
              marginBottom={'5rem'}
              viewMode="live"
            />
          )}
        </>
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Scroll to top
          </Typography>
          <Fab
            color="primary"
            size="medium"
            onClick={scrollToTop}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
            aria-label="scroll to top"
          >
            <KeyboardArrowUp />
          </Fab>
        </Box>
      )}
    </>
  );
}

export default ApplicationTypeTemplate;